import ConciergeHeader from "public/account/survey/concierge/concierge-header.svg";
import CircleCheck from "public/account/survey/concierge/circle-check.svg";
import Radio from "./Input/Radio";
import { useState, useMemo } from "react";
import ConciergeBenefitsModal from "./ConciergeBenefitsModal";
import ConciergeAddOnBG from "public/account/survey/concierge/concierge-invoice-bg.png";
import Image from "next/image";
import { useResponsiveContext } from "context/ResponsiveContext";
import { Services } from "utils/account/services";
import { useAuthContext } from "context/AuthContext";
import { usePropertyContext } from "components/pages/Account/Property/context";
import ButtonV2 from "./ButtonV2";
import { usePathname, useRouter } from "next/navigation";
import { useEventTracker } from "utils/useEventTracker";
import constants from "utils/constants";

const featuresList = [
	{
		label: "Daily exemption checks and alerts",
	},
	{
		label: "Mortgage analysis",
		value: "$914 avg savings",
	},
	{
		label: "Home insurance savings review",
		value: "$439 avg savings",
	},
	{
		label: "Unclaimed property search",
		value: "$532 avg savings",
	},
];

export const useShowConciergeAddOnCard = () => {
	const availableServiceIds = Services.useAvailableServiceIds();
	const { userData } = useAuthContext();
	const { details } = usePropertyContext();

	return useMemo(() => {
		return (
			availableServiceIds.includes("concierge") &&
			userData?.account_tier !== "concierge" &&
			details?.state === "TX"
		);
	}, [availableServiceIds, userData?.account_tier, details?.state]);
};

const ConciergeAddOn = ({
	onConciergeAddOnSelected,
}: {
	onConciergeAddOnSelected: (selected: boolean) => void;
}) => {
	return (
		<div id="concierge-add-on">
			<ConciergeAddOnCard />
			<div className="concierge-add-on-footer mt-2">
				<div className="concierge-add-on-footer-radio-container">
					<Radio
						onChange={() => {
							onConciergeAddOnSelected(true);
						}}
						label="Yes, I want exemption fall off protection."
						name="concierge-add-on"
						smallLabel={true}
					/>
					<p className="small kelly-dark nowrap">+$99 /year</p>
				</div>
				<Radio
					onChange={() => {
						onConciergeAddOnSelected(false);
					}}
					label="No, I don't need exemption fall off protection."
					name="concierge-add-on"
					smallLabel={true}
				/>
			</div>
		</div>
	);
};

export const ConciergeAddOnCard = ({
	variant = "default",
}: {
	variant?: "default" | "cta";
}) => {
	const [showModal, setShowModal] = useState(false);
	const { details } = usePropertyContext();
	const isMobile = useResponsiveContext();
	const { userData } = useAuthContext();
	const pathname = usePathname();
	const router = useRouter();

	const trackEvent = useEventTracker();

	const handleUpgradeToConcierge = async () => {
		trackEvent({
			eventName: constants.EVENTS.CONCIERGE_ADD_ON_CTA_CLICKED,
			data: {
				property_id: details?.id,
				customer_id: userData?.id,
				pathname: pathname,
			},
		});
		router.push(`/account/properties/${details?.id}/concierge`);
	};

	return (
		<div id="concierge-add-on-card">
			<div className="concierge-add-on-card-content">
				<div className="concierge-add-on-container mt-4">
					<div className="concierge-add-on-bg-wrapper">
						<Image src={ConciergeAddOnBG} alt="Concierge Add On Background" />
					</div>
					<p className="extra-small kelly-dark semibold recommended-tag">
						Recommended
					</p>
					<ConciergeHeader className="concierge-icon" />
					<p className="large bold denim">
						Protect My Exemption with Concierge!
					</p>
					<p className="denim_5 sm mt-0_5">
						Never miss out on savings or be caught off guard.
					</p>
					<p className={(isMobile ? "" : "large ") + "bold denim mt-1_5"}>
						Add Exemption Fall Off Protection to Unlock:
					</p>
					<div className="feature-list mt-1">
						{featuresList.map((item, index) => (
							<div className="list-item" key={index}>
								<div className="list-item-label">
									<span className="icon">
										<CircleCheck />
									</span>
									<div className="list-item-label-text">
										<p className="sm">{item.label} </p>
									</div>
								</div>
								{item.value && (
									<p className="list-item-value extra-small semibold">
										{item.value}
									</p>
								)}
							</div>
						))}
					</div>
					<div className="relative z-10 mt-2">
						<p
							className="semibold azure sm mt-2 cursor-pointer"
							onClick={() => setShowModal(true)}>
							View all Concierge benefits +
						</p>
					</div>
					<div className="review-card mt-2 mb-1">
						<p className="sm denim">
							“Before joining Ownwell we had absolutely no clue our exemption
							had been removed for the past 2 years. After signing up for
							Concierge they alerted me of the problem and got a refund for over
							$4,000 in savings I had missed out on!”
						</p>
						<p className="extra-small semibold mt-1">
							Mark B. - Ownwell Concierge Customer
						</p>
					</div>
					{variant === "cta" && (
						<div className="flex justify-end relative z-10 mt-2">
							<div className="cta">
								<ButtonV2
									variant="primary"
									size="extra-small"
									onClick={handleUpgradeToConcierge}>
									Upgrade to Concierge
								</ButtonV2>
							</div>
						</div>
					)}
				</div>
			</div>
			{showModal && (
				<ConciergeBenefitsModal onAttemptExit={() => setShowModal(false)} />
			)}
		</div>
	);
};

export default ConciergeAddOn;
